import { FilterType, MenuItem } from '../models';

export function filterMenuItems(items: MenuItem[], searchTerm: string, filterBy?: FilterType): MenuItem[] {
  if (!items || items.length === 0) {
    return [];
  }
  const filtered: MenuItem[] = [];
  const searchTermLower = searchTerm.toLowerCase();

  for (const item of items) {
    let matchesCurrentItem = false;
    let filteredChildren: MenuItem[] = [];

    if (item.items && item.items.length > 0) {
      filteredChildren = filterMenuItems(item.items, searchTerm, filterBy);
    }

    switch (filterBy) {
      case 'favorites':
        matchesCurrentItem = item.liked === true;
        if (matchesCurrentItem && searchTermLower) {
          matchesCurrentItem =
            item.title.toLowerCase().includes(searchTermLower) ||
            (item.link ? item.link.toLowerCase().includes(searchTermLower) : false);
        }
        break;
      case 'all':
      default:
        if (searchTermLower === '') {
          matchesCurrentItem = true;
        } else {
          matchesCurrentItem =
            item.title.toLowerCase().includes(searchTermLower) ||
            (item.link ? item.link.toLowerCase().includes(searchTermLower) : false);
        }
        break;
    }

    if (matchesCurrentItem || filteredChildren.length > 0) {
      const newItem: MenuItem = { ...item, isExpanded: filterBy === 'all' && searchTerm === '' ? false : true };

      if (filteredChildren.length > 0) {
        newItem.items = filteredChildren;
      } else if (matchesCurrentItem && item.items && item.items.length > 0) {
        newItem.items = [...item.items];
      } else if (matchesCurrentItem && item.items) {
        newItem.items = [];
      }

      filtered.push(newItem);
    }
  }

  return filtered;
}

export function searchMenuItemsFlat(
  items: MenuItem[],
  searchTerm: string,
  parentInfo?: { title: string; description?: string }
): MenuItem[] {
  if (!searchTerm || searchTerm.trim() === '') {
    return items;
  }

  const results: MenuItem[] = [];
  const searchTermLower = searchTerm.toLowerCase().trim();

  for (const item of items) {
    const itemMatches =
      item.title.toLowerCase().includes(searchTermLower) ||
      (item.description && item.description.toLowerCase().includes(searchTermLower)) ||
      (item.link && item.link.toLowerCase().includes(searchTermLower));

    if (itemMatches && item.link) {
      const resultItem = {
        ...item,
        parentTitle: parentInfo?.title,
        parentDescription: parentInfo?.description
      };
      results.push(resultItem);
    }

    if (item.items && item.items.length > 0) {
      const childResults = searchMenuItemsFlat(item.items, searchTerm, {
        title: item.title,
        description: item.description
      });
      results.push(...childResults);
    }
  }

  return results;
}

export function resetExpandedState(items: MenuItem[]): MenuItem[] {
  return items.map((item) => {
    const newItem = { ...item, isExpanded: false };
    if (newItem.items && newItem.items.length > 0) {
      newItem.items = resetExpandedState(newItem.items);
    }
    return newItem;
  });
}

export function collectFavoriteItems(items: MenuItem[], parentTitle?: string, parentDescription?: string): MenuItem[] {
  const favorites: MenuItem[] = [];

  for (const item of items) {
    if (item.liked && item.link) {
      const favoriteItem = {
        ...item,
        parentTitle: parentTitle,
        parentDescription: parentDescription
      };
      favorites.push(favoriteItem);
    }

    if (item.items && item.items.length > 0) {
      const childFavorites = collectFavoriteItems(item.items, item.title, item.description);
      favorites.push(...childFavorites);
    }
  }

  return favorites;
}

export function collectFavoritesByRootParent(items: MenuItem[]): Array<{ parent: MenuItem; favorites: MenuItem[] }> {
  const grouped: Array<{ parent: MenuItem; favorites: MenuItem[] }> = [];

  const findFavorites = (
    items: MenuItem[],
    rootParent: MenuItem | null = null,
    directParent: MenuItem | null = null
  ) => {
    items.forEach((item) => {
      const actualRootParent = rootParent || item;

      if (item.liked && item.link) {
        const existingGroup = grouped.find((g) => g.parent.title === actualRootParent.title);
        const favoriteWithParentInfo = {
          ...item,
          parentTitle: directParent?.title,
          parentDescription: directParent?.description
        };

        if (existingGroup) {
          existingGroup.favorites.push(favoriteWithParentInfo);
        } else {
          grouped.push({
            parent: actualRootParent,
            favorites: [favoriteWithParentInfo]
          });
        }
      }
      if (item.items && item.items.length > 0) {
        findFavorites(item.items, actualRootParent, item);
      }
    });
  };

  findFavorites(items);
  return grouped;
}
